import { memo } from "react";
import { motion } from "framer-motion";
import { ImageSquare, ArrowsClockwise } from "@phosphor-icons/react";

/**
 * StepGuideCards
 *
 * Renders a 3–5 step visual how-to guide produced by the
 * `generate_step_guide` LLM tool. Each card has a flat-icon
 * image (or a graceful fallback if generation failed) plus
 * a short label and instruction.
 *
 * Mobile-first: cards stack vertically on small screens, two
 * columns from sm and three from lg to keep cards readable.
 *
 * V9.30 — Image-render performance pass:
 *  - Persisted steps (with `image_ready: true` from history reload) are
 *    fetched via the cache-friendly URL endpoint
 *    `/api/chat/messages/{id}/step-image/{idx}` so the browser can
 *    lazy-load, cache, and incrementally decode.
 *  - Live-stream steps (no message_id yet) fall back to the inline
 *    `image_b64` data URL — that's only on the very first render
 *    pass while the message exists only in memory.
 *  - StepCard is memoised — during token streaming the parent
 *    re-renders dozens of times per turn and StepCard is
 *    visually expensive (decoded PNG); memo lets React skip work
 *    when the step payload hasn't changed.
 *
 * Props:
 *  - guide: { title, intro, steps: [{label, instruction, image_b64?, image_ready?, image_failed?, regenerating?}] }
 *  - messageId: string | null  → required to build the URL
 *      endpoint for persisted images. `null` while a turn is
 *      still streaming (tmp id) — falls back to inline base64.
 *  - status: { completed, total, message, kind } | null  → shows a friendly
 *            first-person progress badge while images are being generated.
 *  - onRegenerate: (stepIndex) => void  → called when a card's redraw
 *                  button is pressed. If null/undefined the button is hidden.
 */
export default function StepGuideCards({ guide, messageId, status, onRegenerate }) {
  if (!guide || !Array.isArray(guide.steps) || guide.steps.length === 0) return null;

  const showStatus = status && status.kind === "working";
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";

  return (
    <div className="mt-3 w-full" data-testid="step-guide">
      <div className="mb-2.5">
        <div
          className="text-[13px] font-medium text-cyan-300/90 tracking-tight"
          data-testid="step-guide-title"
        >
          {guide.title}
        </div>
        {guide.intro ? (
          <div className="text-[12px] text-white/55 mt-0.5">{guide.intro}</div>
        ) : null}
        {showStatus && (
          <div
            className="mt-2 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-[11px] text-cyan-200/90"
            data-testid="step-guide-status"
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75 animate-ping" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-cyan-400" />
            </span>
            <span>
              {status.message}
              {status.total > 0 && (
                <span className="text-white/50 ml-1">
                  · {status.completed} of {status.total} ready
                </span>
              )}
            </span>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {guide.steps.map((s, i) => {
          // Prefer the cache-friendly URL endpoint when the message
          // is persisted (has a real id) AND the backend confirmed
          // it stored an image for this step (`image_ready`). The
          // `?v=` cache-bust uses the per-step version that
          // increments on regenerate, so a fresh redraw shows up
          // even after the browser cached the previous URL.
          const v = s.image_v || 0;
          const persistedUrl =
            messageId && s.image_ready
              ? `${apiBase}/api/chat/messages/${messageId}/step-image/${i}${v ? `?v=${v}` : ""}`
              : null;
          return (
            <StepCard
              key={i}
              step={s}
              index={i}
              persistedUrl={persistedUrl}
              onRegenerate={onRegenerate ? () => onRegenerate(i) : null}
            />
          );
        })}
      </div>
    </div>
  );
}

const StepCard = memo(function StepCard({ step, index, persistedUrl, onRegenerate }) {
  const hasImage = !!persistedUrl || !!step.image_b64;
  const isLoading = !hasImage && !step.image_failed;
  const isRegenerating = step.regenerating === true;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: Math.min(index * 0.05, 0.25) }}
      className="rounded-xl bg-[#0f0f0f] border border-white/10 overflow-hidden hover:border-cyan-500/30 transition-colors"
      data-testid={`step-guide-card-${index}`}
    >
      <div className="aspect-square w-full bg-[#0a0a0a] border-b border-white/5 flex items-center justify-center relative group">
        {hasImage ? (
          <img
            // Persisted URL wins (cacheable, lazy-loadable, async-
            // decodable). Inline base64 is the live-stream fallback.
            src={persistedUrl || `data:image/png;base64,${step.image_b64}`}
            alt={step.label}
            // V9.30 — These three attrs were no-ops on data: URLs.
            // With a real URL they unlock browser-native deferral
            // and off-main-thread decode so several cards arriving
            // at once don't jank the chat scroll on mobile.
            loading="lazy"
            decoding="async"
            fetchpriority="low"
            className="w-full h-full object-cover"
            data-testid={`step-guide-image-${index}`}
          />
        ) : step.image_failed && !isRegenerating ? (
          <div className="flex flex-col items-center gap-1.5 text-white/30">
            <ImageSquare size={26} weight="duotone" />
            <span className="text-[10px]">No visual</span>
          </div>
        ) : (
          <div
            // V9.31 — Numbered placeholder. While Gemini is drawing,
            // show a big bold step number on a subtle cyan-tinted
            // gradient so the order is INSTANTLY clear and the wait
            // feels intentional rather than blank. Replaces the
            // tiny spinner that previously dominated the card.
            className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-cyan-500/10 via-[#0a0f14] to-[#080808]"
            data-testid={`step-guide-loading-${index}`}
          >
            <div className="text-[44px] sm:text-[52px] font-display font-light text-cyan-300/35 leading-none select-none">
              {index + 1}
            </div>
            <div className="mt-3 flex items-center gap-2 text-[10px] text-white/35">
              <div className="w-3 h-3 rounded-full border-2 border-cyan-400/30 border-t-cyan-400 animate-spin" />
              <span>{isRegenerating ? "Redrawing…" : "Drawing…"}</span>
            </div>
          </div>
        )}
        {/* V9.31 — CSS step badge only when no image yet AND not the
            big loading placeholder (i.e. for `image_failed` fallback).
            When the image IS present, the number is baked INTO the
            PNG by the backend so screenshots / MMS recipients also
            see the order. Removing the CSS badge on top of the image
            avoids visual double-numbering. */}
        {step.image_failed && !isRegenerating && (
          <div className="absolute top-1.5 left-1.5 w-5 h-5 rounded-full bg-cyan-500/90 text-[11px] font-semibold text-[#001017] flex items-center justify-center shadow-sm">
            {index + 1}
          </div>
        )}
        {/* Regenerate icon button — visible whenever a card has settled
            (success or failure). Hidden during initial draw / redraw to
            avoid double-trigger and visual clutter. */}
        {onRegenerate && !isLoading && !isRegenerating && (
          <button
            type="button"
            onClick={onRegenerate}
            className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/55 border border-white/15 hover:bg-cyan-500/20 hover:border-cyan-400/50 text-white/70 hover:text-cyan-200 flex items-center justify-center backdrop-blur-sm opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
            title="Regenerate this image"
            aria-label="Regenerate this image"
            data-testid={`step-guide-regenerate-${index}`}
          >
            <ArrowsClockwise size={12} weight="bold" />
          </button>
        )}
      </div>
      <div className="px-3 py-2.5">
        <div
          className="text-[13px] font-medium text-white/90 leading-snug"
          data-testid={`step-guide-label-${index}`}
        >
          {step.label}
        </div>
        <div
          className="text-[12px] text-white/60 mt-1 leading-relaxed"
          data-testid={`step-guide-instruction-${index}`}
        >
          {step.instruction}
        </div>
      </div>
    </motion.div>
  );
});
